import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "@/lib/link";
import { Menu, X, Globe, MessageCircle, ChevronDown, Check } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LOCALES, parseLocalePath, buildLocaleUrl, getLocaleMeta } from "@/i18n/config";

export default function Header() {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const { locale: currentLocale, rest: currentPath } = parseLocalePath(location.pathname);
    const currentMeta = getLocaleMeta(currentLocale);

    const primaryLinks = [
        { to: "/", label: t("nav.home"), id: NAV.home },
        { to: "/about", label: t("nav.about"), id: NAV.about },
        { to: "/products", label: t("nav.products"), id: NAV.products },
        { to: "/industries", label: t("nav.industries"), id: NAV.industries },
        { to: "/export-solutions", label: t("nav.solutions"), id: NAV.solutions },
    ];
    const moreLinks = [
        { to: "/custom-sourcing", label: t("nav.customSourcing"), id: "nav-sourcing" },
        { to: "/import-business", label: t("nav.importBusiness"), id: "nav-import-business" },
        { to: "/global-partner", label: t("nav.partner"), id: "nav-partner" },
        { to: "/government", label: t("nav.government"), id: "nav-government" },
        { to: "/resources", label: t("nav.resources"), id: "nav-resources" },
        { to: "/exportfy-ai", label: t("nav.ai"), id: "nav-ai" },
        { to: "/contact", label: t("nav.contact"), id: "nav-contact" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const switchLocale = (code) => {
        navigate(buildLocaleUrl(currentPath || "/", code));
    };

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
                    : "bg-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between h-16 sm:h-20">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] via-[#0A2540] to-[#059669] flex items-center justify-center shadow-lg shadow-blue-950/20">
                        <span className="text-white font-extrabold text-lg">E</span>
                    </div>
                    <div className={`font-extrabold text-lg tracking-tight ${scrolled ? "text-[#0A2540]" : "text-white"}`}>
                        Exportfy<span className="text-[#059669]">.world</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {primaryLinks.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.id}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                                    scrolled ? "text-[#0A2540] hover:text-[#1D4ED8]" : "text-white/90 hover:text-white"
                                } ${isActive ? (scrolled ? "text-[#1D4ED8]" : "text-white") : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className={`px-3 py-2 text-sm font-semibold rounded-md inline-flex items-center gap-1 ${
                                    scrolled ? "text-[#0A2540] hover:text-[#1D4ED8]" : "text-white/90 hover:text-white"
                                }`}
                                data-testid="nav-more"
                            >
                                {t("nav.more")} <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            {moreLinks.map((m) => (
                                <DropdownMenuItem key={m.to} asChild data-testid={m.id}>
                                    <Link to={m.to} className="w-full">{m.label}</Link>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid={NAV.languageSelect}
                                className={`px-3 py-2 rounded-md inline-flex items-center gap-1.5 text-sm font-semibold ${
                                    scrolled ? "text-[#0A2540] hover:bg-slate-100" : "text-white/90 hover:bg-white/10"
                                }`}
                            >
                                <Globe className="w-4 h-4" strokeWidth={1.75} />
                                <span className="uppercase">{currentLocale}</span>
                                <ChevronDown className="w-3 h-3" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 max-h-[70vh] overflow-y-auto">
                            <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500">{t("nav.language")}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {LOCALES.map((l) => (
                                <DropdownMenuItem
                                    key={l.code}
                                    onClick={() => switchLocale(l.code)}
                                    data-testid={`lang-switch-${l.code}`}
                                    className="flex items-center justify-between cursor-pointer"
                                >
                                    <span>{l.native}</span>
                                    {l.code === currentLocale && <Check className="w-4 h-4 text-emerald-600" />}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <a
                        href="https://wa.me/919918000070"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={NAV.whatsapp}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
                            scrolled ? "bg-slate-100 hover:bg-slate-200 text-[#0A2540]" : "bg-white/10 hover:bg-white/20 text-white"
                        }`}
                        aria-label="WhatsApp"
                    >
                        <MessageCircle className="w-4.5 h-4.5" strokeWidth={1.75} />
                    </a>

                    <Link to="/consultation" data-testid={NAV.bookCta}>
                        <Button className="rounded-full bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#1D4ED8] hover:to-[#1E3A8A] text-white font-semibold px-5 shadow-lg shadow-blue-600/20">
                            {t("nav.bookConsultation")}
                        </Button>
                    </Link>
                </div>

                <button
                    className={`lg:hidden p-2 rounded-md ${scrolled ? "text-[#0A2540]" : "text-white"}`}
                    onClick={() => setOpen(!open)}
                    data-testid={NAV.mobileToggle}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-white border-t border-slate-200 max-h-[80vh] overflow-y-auto">
                    <div className="container-x py-4 flex flex-col gap-1">
                        {[...primaryLinks, ...moreLinks, { to: "/consultation", label: t("nav.bookConsultation"), id: "nav-mobile-consultation" }].map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={l.id ? `mobile-${l.id}` : undefined}
                                className={({ isActive }) =>
                                    `px-3 py-3 rounded-md font-semibold text-[#0A2540] hover:bg-slate-100 ${
                                        isActive ? "bg-slate-100 text-[#1D4ED8]" : ""
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="mt-3 pt-3 border-t border-slate-100">
                            <div className="px-3 text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">{t("nav.language")} · {currentMeta.native}</div>
                            <div className="flex flex-wrap gap-1">
                                {LOCALES.map((l) => (
                                    <button
                                        key={l.code}
                                        onClick={() => switchLocale(l.code)}
                                        data-testid={`mobile-lang-switch-${l.code}`}
                                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                            l.code === currentLocale
                                                ? "bg-[#0A2540] text-white border-[#0A2540]"
                                                : "bg-white text-[#0A2540] border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                        {l.native}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
